import { compressImageFileToDataUrl } from "@/lib/imageUpload";

export interface ArticleShopContentFields {
  shop_description: string;
  shop_image_url: string | null;
}

interface ArticleShopContentSectionProps {
  value: ArticleShopContentFields;
  onChange: (value: ArticleShopContentFields) => void;
  onError?: (message: string | null) => void;
  articleName: string;
  canEdit: boolean;
}

export function ArticleShopContentSection({
  value,
  onChange,
  onError,
  articleName,
  canEdit,
}: ArticleShopContentSectionProps) {
  async function onImageSelected(file: File | null) {
    if (!file || !canEdit) return;
    if (!file.type.startsWith("image/")) {
      onError?.("Bitte wählen Sie eine Bilddatei (PNG, JPEG oder WebP).");
      return;
    }
    try {
      const imageData = await compressImageFileToDataUrl(file);
      onChange({ ...value, shop_image_url: imageData });
      onError?.(null);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : "Bild konnte nicht geladen werden.");
    }
  }

  return (
    <fieldset className="space-y-4 rounded-lg border border-brand-100 bg-brand-50/40 p-4">
      <legend className="px-1 text-sm font-semibold text-label">Gäste-Shop Inhalte</legend>
      <p className="text-xs text-label-secondary">
        Diese Inhalte werden Gästen im Vorbestell-Shop angezeigt.
      </p>

      <label className="flex flex-col gap-1 text-sm">
        Shop-Beschreibung
        <textarea
          rows={4}
          className="eh-input"
          disabled={!canEdit}
          placeholder="Beschreibung für Gäste, z. B. Zutaten, Abholhinweise …"
          value={value.shop_description}
          onChange={(e) => onChange({ ...value, shop_description: e.target.value })}
        />
      </label>

      <div className="space-y-2">
        <span className="text-sm font-medium text-label">Produktbild</span>
        {value.shop_image_url ? (
          <div className="flex flex-wrap items-start gap-4">
            <img
              src={value.shop_image_url}
              alt={articleName}
              className="h-32 w-32 rounded-lg border border-separator object-cover"
            />
            {canEdit ? (
              <button
                type="button"
                className="text-sm text-status-red hover:underline"
                onClick={() => onChange({ ...value, shop_image_url: null })}
              >
                Bild entfernen
              </button>
            ) : null}
          </div>
        ) : canEdit ? (
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-separator bg-white px-4 py-8 text-center text-sm text-label-secondary hover:border-brand-300 hover:bg-brand-50/30">
            <span className="font-medium text-label">Bild hochladen</span>
            <span className="text-xs">JPEG, PNG oder WebP · max. 2 MB</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="sr-only"
              onChange={(e) => void onImageSelected(e.target.files?.[0] ?? null)}
            />
          </label>
        ) : (
          <p className="text-sm text-label-secondary">Kein Bild hinterlegt.</p>
        )}
      </div>
    </fieldset>
  );
}
